import React, { useEffect, useRef, useState } from 'react';
import { FaExternalLinkAlt } from 'react-icons/fa';

interface ResultGridProps {
    columns: string[];
    rows: (string | null)[][];
    /** Column types for the header, by index (the rows grid has them from the table list; a query result does not). */
    types?: (string | null)[];
    /** Shown under the grid when the answer was cut short. */
    truncatedText?: string | null;
    /** Shown in the body when there are no rows. */
    emptyText: string;
    /** Names the grid for assistive technology. */
    label: string;
    /** DESIGN_EDITING.md console flow 1: when set, each header is a button that sorts by its column. */
    onSort?: (column: string) => void;
    sort?: { column: string; dir: 'asc' | 'desc' } | null;
    /** Console flow 5: a click on a row this says yes to opens Edit row. */
    onRowClick?: (r: number) => void;
    rowClickable?: (r: number) => boolean;
    /** What a clickable row's tooltip says (Edit row unless the grid is a picker). */
    rowTitle?: string;
    /** E27: the columns whose cells point at another table's row; the arrow beside a value opens Related row. */
    related?: { columns: ReadonlySet<number>; open: (r: number, c: number) => void };
    /** Console flow 6: a checkbox per row this says yes to, and one for the page. */
    selection?: {
        checked: ReadonlySet<number>;
        selectable: (r: number) => boolean;
        onToggle: (r: number) => void;
        onToggleAll: (on: boolean) => void;
    };
    /** Console flow 7: a caret per column header, offering Rename and (unless a key column) Delete. */
    columnMenu?: { onRename: (column: string) => void; onDelete: (column: string) => void; canDelete: (column: string) => boolean };
    /**
     * E26: a double-click edits a cell in place when `kind` says how. Enter saves (a yes/no cell saves on choosing);
     * Escape or a click elsewhere cancels and writes nothing. `onSave` answers true when the cell may close (saved, or
     * an outcome that read the page again), false to keep it open with what was typed.
     */
    cellEdit?: {
        kind: (r: number, c: number) => 'text' | 'yesno' | null;
        nullable: (c: number) => boolean;
        onSave: (r: number, c: number, text: string) => Promise<boolean>;
    };
}

/** How long a click on a row waits for a second click (a cell's double-click) before it opens Edit row. */
export const ROW_CLICK_DELAY_MS = 250;

/**
 * The ONE renderer for a table's rows and a query's result (DESIGN_V1.md D13).
 *
 * ⛔ EVERY CELL AND EVERY COLUMN NAME IS THE CUSTOMER'S OWN DATA, rendered as React text and nothing else: never
 *    dangerouslySetInnerHTML, never a link or an image built from a value. A cell holding `<script>` shows those
 *    characters (ResultGrid.test.tsx). NULL is shown as a marker, so it never looks like an empty string.
 * Keys are indexes, because a query can legally return two columns with the same name.
 */
const ResultGrid: React.FC<ResultGridProps> = ({
    columns, rows, types, truncatedText, emptyText, label, onSort, sort, onRowClick, rowClickable, selection, columnMenu, cellEdit,
    rowTitle = 'Edit row', related,
}) => {
    const [menuColumn, setMenuColumn] = useState<string | null>(null);
    // E26: the cell being edited, what it holds now, and whether its save is out
    const [editing, setEditing] = useState<{ r: number; c: number; text: string } | null>(null);
    const [saving, setSaving] = useState(false);
    // read by blur and Escape, which can fire in the same tick as the save starts (a disabled input loses focus)
    const savingRef = useRef(false);
    const clickTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
    const inputRef = useRef<HTMLInputElement | null>(null);
    useEffect(() => () => { if (clickTimer.current) clearTimeout(clickTimer.current); }, []);
    // A new page (another table, the next page, a re-read after a save) ends any edit
    useEffect(() => { setEditing(null); setSaving(false); savingRef.current = false; }, [rows]);

    const rowClicked = (r: number) => {
        if (!cellEdit) { onRowClick!(r); return; }
        if (clickTimer.current) clearTimeout(clickTimer.current);
        clickTimer.current = setTimeout(() => { clickTimer.current = null; onRowClick!(r); }, ROW_CLICK_DELAY_MS);
    };
    const cellDoubleClicked = (r: number, c: number) => {
        if (!cellEdit) return;
        if (clickTimer.current) { clearTimeout(clickTimer.current); clickTimer.current = null; }
        const kind = cellEdit.kind(r, c);
        if (kind === null) { if (onRowClick && (rowClickable ? rowClickable(r) : true)) onRowClick(r); return; }
        const v = rows[r][c];
        setEditing({ r, c, text: v === null ? '' : v });
    };
    const save = async (text: string) => {
        if (!editing || !cellEdit || savingRef.current) return;
        savingRef.current = true;
        setSaving(true);
        const at = editing;
        const close = await cellEdit.onSave(at.r, at.c, text);
        savingRef.current = false;
        setSaving(false);
        if (close) setEditing(null);
        else {
            setEditing({ ...at, text });   // refused: still open, with what was typed, and the caret back in it
            setTimeout(() => inputRef.current?.focus(), 0);
        }
    };
    const cancel = () => { if (!savingRef.current) setEditing(null); };
    if (columns.length === 0) {
        return <p className="db-data-note">Done.</p>;
    }
    const selectableRows = selection ? rows.map((_, r) => r).filter((r) => selection.selectable(r)) : [];
    const allChecked = selectableRows.length > 0 && selectableRows.every((r) => selection!.checked.has(r));
    return (
        <>
            <div className="db-data-grid-wrap">
                <table className="db-data-grid" aria-label={label}>
                    <thead>
                        <tr>
                            {selection && (
                                <th scope="col" className="db-data-check">
                                    <input
                                        type="checkbox"
                                        aria-label="Select every row on this page"
                                        checked={allChecked}
                                        disabled={selectableRows.length === 0}
                                        onChange={(e) => selection.onToggleAll(e.target.checked)}
                                    />
                                </th>
                            )}
                            {columns.map((name, i) => {
                                const sorted = sort && sort.column === name ? sort.dir : null;
                                const inner = (
                                    <>
                                        <span className="db-data-col-name">{name}</span>
                                        {sorted && <span className="db-data-col-sort" aria-hidden="true">{sorted === 'asc' ? '▲' : '▼'}</span>}
                                        {types?.[i] && <span className="db-data-col-type">{types[i]}</span>}
                                    </>
                                );
                                return (
                                    <th
                                        key={i}
                                        scope="col"
                                        title={types?.[i] ? `${name} (${types[i]})` : name}
                                        {...(onSort ? { 'aria-sort': sorted === 'asc' ? 'ascending' : sorted === 'desc' ? 'descending' : 'none' } as const : {})}
                                    >
                                        {onSort ? (
                                            <button type="button" className="db-data-sort-button" onClick={() => onSort(name)} aria-label={`Sort by ${name}`}>
                                                {inner}
                                            </button>
                                        ) : inner}
                                        {columnMenu && (
                                            <span className="db-data-col-menu">
                                                <button type="button" className="db-data-col-menu-caret" aria-label={`Column ${name}`} title="Column"
                                                    aria-haspopup="menu" aria-expanded={menuColumn === name}
                                                    onClick={() => setMenuColumn((c) => (c === name ? null : name))}>▾</button>
                                                {menuColumn === name && (
                                                    <div className="db-data-col-menu-items" role="menu">
                                                        <button type="button" role="menuitem" onClick={() => { setMenuColumn(null); columnMenu.onRename(name); }}>Rename</button>
                                                        {columnMenu.canDelete(name) && (
                                                            <button type="button" role="menuitem" onClick={() => { setMenuColumn(null); columnMenu.onDelete(name); }}>Delete</button>
                                                        )}
                                                    </div>
                                                )}
                                            </span>
                                        )}
                                    </th>
                                );
                            })}
                        </tr>
                    </thead>
                    <tbody>
                        {rows.length === 0 ? (
                            <tr>
                                <td colSpan={columns.length + (selection ? 1 : 0)} className="db-data-empty">{emptyText}</td>
                            </tr>
                        ) : rows.map((row, r) => {
                            const clickable = !!onRowClick && (rowClickable ? rowClickable(r) : true);
                            return (
                            <tr
                                key={r}
                                {...(clickable ? {
                                    className: 'db-data-row-clickable', tabIndex: 0, title: rowTitle,
                                    onClick: () => rowClicked(r),
                                    onKeyDown: (e: React.KeyboardEvent) => { if (e.key === 'Enter' && e.target === e.currentTarget) onRowClick!(r); },
                                } : {})}
                            >
                                {selection && (
                                    <td className="db-data-check" onClick={(e) => e.stopPropagation()}>
                                        <input
                                            type="checkbox"
                                            aria-label={`Select row ${r + 1}`}
                                            checked={selection.checked.has(r)}
                                            disabled={!selection.selectable(r)}
                                            onChange={() => selection.onToggle(r)}
                                        />
                                    </td>
                                )}
                                {columns.map((col, c) => {
                                    const cell = row[c];
                                    if (editing && editing.r === r && editing.c === c) {
                                        const kind = cellEdit!.kind(r, c);
                                        return (
                                            <td key={c} className="db-data-cell-editing" onClick={(e) => e.stopPropagation()}>
                                                {kind === 'yesno' ? (
                                                    <select
                                                        aria-label={`Edit ${col}`}
                                                        autoFocus
                                                        disabled={saving}
                                                        value={editing.text}
                                                        onChange={(e) => { setEditing({ ...editing, text: e.target.value }); void save(e.target.value); }}
                                                        onKeyDown={(e) => { if (e.key === 'Escape') cancel(); }}
                                                        onBlur={cancel}
                                                    >
                                                        {editing.text === '' && !cellEdit!.nullable(c) && <option value="" disabled>Choose</option>}
                                                        <option value="t">Yes</option>
                                                        <option value="f">No</option>
                                                        {cellEdit!.nullable(c) && <option value="">NULL</option>}
                                                    </select>
                                                ) : (
                                                    <input
                                                        ref={inputRef}
                                                        aria-label={`Edit ${col}`}
                                                        autoFocus
                                                        onFocus={(e) => e.currentTarget.select()}
                                                        disabled={saving}
                                                        value={editing.text}
                                                        onChange={(e) => setEditing({ ...editing, text: e.target.value })}
                                                        onKeyDown={(e) => {
                                                            if (e.key === 'Enter') { e.preventDefault(); void save(editing.text); }
                                                            if (e.key === 'Escape') { e.preventDefault(); cancel(); }
                                                        }}
                                                        onBlur={cancel}
                                                    />
                                                )}
                                            </td>
                                        );
                                    }
                                    const editable = !!cellEdit && cellEdit.kind(r, c) !== null;
                                    const dbl = cellEdit ? { onDoubleClick: (e: React.MouseEvent) => { e.stopPropagation(); cellDoubleClicked(r, c); } } : {};
                                    if (cell === null) {
                                        return <td key={c} title="NULL" className={editable ? 'db-data-cell-editable' : undefined} {...dbl}><span className="db-data-null">NULL</span></td>;
                                    }
                                    if (related && related.columns.has(c)) {
                                        return (
                                            <td key={c} title={cell ?? ''} className={editable ? 'db-data-cell-editable' : undefined} {...dbl}>
                                                <span className="db-data-fk-cell">
                                                    <span className="db-data-fk-value">{cell ?? ''}</span>
                                                    <button
                                                        type="button"
                                                        className="db-data-fk-open"
                                                        aria-label={`Related row for ${col} ${cell ?? ''}`}
                                                        title="Related row"
                                                        onClick={(e) => { e.stopPropagation(); related.open(r, c); }}
                                                        onDoubleClick={(e) => e.stopPropagation()}
                                                    >
                                                        <FaExternalLinkAlt aria-hidden="true" />
                                                    </button>
                                                </span>
                                            </td>
                                        );
                                    }
                                    return <td key={c} title={cell ?? ''} className={editable ? 'db-data-cell-editable' : undefined} {...dbl}>{cell ?? ''}</td>;
                                })}
                            </tr>
                            );
                        })}
                    </tbody>
                </table>
            </div>
            {truncatedText && <p className="db-data-note">{truncatedText}</p>}
        </>
    );
};

export default ResultGrid;
